import React, { useState } from 'react';

export function ContactCTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    unitCount: '',
    propertyType: '',
    timeline: '',
    additionalInfo: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('name', formData.name);
    formDataToSubmit.append('email', formData.email);
    formDataToSubmit.append('phone', formData.phone);
    formDataToSubmit.append('propertyAddress', formData.propertyAddress);
    formDataToSubmit.append('unitCount', formData.unitCount);
    formDataToSubmit.append('propertyType', formData.propertyType);
    formDataToSubmit.append('timeline', formData.timeline);
    formDataToSubmit.append('additionalInfo', formData.additionalInfo);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5qlmdmtc768qf4bqar0', {
        method: 'POST',
        body: formDataToSubmit,
      });

      if (response.ok) {
        alert('Thank you! We have received your information and will contact you within 24 hours with your cash offer.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyAddress: '',
          unitCount: '',
          propertyType: '',
          timeline: '',
          additionalInfo: ''
        });
      } else {
        alert('There was an error submitting your form. Please try again or call us directly.');
      }
    } catch (error) {
      alert('There was an error submitting your form. Please try again or call us directly.');
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ready to Sell Your Apartment Building?
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Get your no-obligation cash offer today. Our experienced team will evaluate your apartment building 
            and provide you with a competitive offer within 24 hours. Start the process now with our simple form.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-gray-50 rounded-lg p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Property Owners Choose Us</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex-shrink-0 mr-4 flex items-center justify-center">
                    <i className="ri-time-line text-white"></i>
                  </div>
                  <span className="text-gray-700">24-hour response guarantee on all inquiries</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex-shrink-0 mr-4 flex items-center justify-center">
                    <i className="ri-money-dollar-circle-line text-white"></i>
                  </div>
                  <span className="text-gray-700">Cash offers with no financing contingencies</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex-shrink-0 mr-4 flex items-center justify-center">
                    <i className="ri-tools-line text-white"></i>
                  </div>
                  <span className="text-gray-700">Buy properties in any condition - no repairs needed</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex-shrink-0 mr-4 flex items-center justify-center">
                    <i className="ri-calendar-check-line text-white"></i>
                  </div>
                  <span className="text-gray-700">Close in as little as 7-14 days</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex-shrink-0 mr-4 flex items-center justify-center">
                    <i className="ri-shield-check-line text-white"></i>
                  </div>
                  <span className="text-gray-700">No fees, commissions, or hidden costs</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden">
              <img 
                src="https://public.readdy.ai/ai/img_res/b8f79d71b5df7ef2e604524b00161767.jpg"
                alt="Older apartment building we purchase"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="bg-white border-2 border-gray-200 rounded-lg p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Your Cash Offer Today</h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="apartment-building-cash-offer">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Units</label>
                  <select
                    name="unitCount"
                    value={formData.unitCount}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select Unit Count</option>
                    <option value="2">2 Units (Duplex)</option>
                    <option value="3-4">3-4 Units</option>
                    <option value="5-10">5-10 Units</option>
                    <option value="11-25">11-25 Units</option>
                    <option value="26-50">26-50 Units</option>
                    <option value="50+">50+ Units</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Property Address *</label>
                <input
                  type="text"
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleChange}
                  placeholder="Street Address, City, State, ZIP"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Property Type</label>
                  <select
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select Property Type</option>
                    <option value="duplex">Duplex</option>
                    <option value="triplex">Triplex</option>
                    <option value="fourplex">Fourplex</option>
                    <option value="small-apartment">Small Apartment Building</option>
                    <option value="large-apartment">Large Apartment Complex</option>
                    <option value="mixed-use">Mixed-Use Building</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Timeline to Sell</label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select Timeline</option>
                    <option value="asap">ASAP (Within 30 days)</option>
                    <option value="1-3months">1-3 Months</option>
                    <option value="3-6months">3-6 Months</option>
                    <option value="6+months">6+ Months</option>
                    <option value="just-exploring">Just Exploring Options</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Information</label>
                <textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Tell us about your property condition, rental income, or any special circumstances..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                  maxLength={500}
                ></textarea>
                <div className="text-xs text-gray-500 mt-1">
                  {formData.additionalInfo.length}/500 characters
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Get My Cash Offer Now
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                By submitting this form, you agree to receive communication about your property. 
                We respect your privacy and never share your information.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
